// Marketing homepage content. Copy is sourced from the 2026 Product Overview.
// Edit copy here; components in src/pages/Home render it.

import type {
  IntegrationLogo,
  MagicPillar,
  BusinessNumber,
  Channel,
  ShipStep,
  SyncPhase,
  EconomicsColumn,
  UseCase,
  HomeIndustry,
} from "@/types/homepage";

/** Tools the genie already plugs into — logo strip under the hero. */
export const INTEGRATION_LOGOS: IntegrationLogo[] = [
  { id: "slack", name: "Slack", logo: "/platform-icons/slack-new-logo-icon-11609376883z32jbkf8kg.png" },
  { id: "teams", name: "Microsoft Teams", logo: "/platform-icons/Microsoft_Office_Teams_(2018–present).svg.png" },
  { id: "whatsapp", name: "WhatsApp", logo: "/platform-icons/WhatsApp.svg.png" },
  { id: "telegram", name: "Telegram", logo: "/platform-icons/Telegram_2019_Logo.svg.png" },
  { id: "zapier", name: "Zapier", logo: "/platform-icons/Zapier-logo.png" },
  { id: "make", name: "Make", logo: "/platform-icons/images.png" },
];

/** "Talk to a live Help Genie" demo line (from the Product Overview). */
export const TALK_TO_GENIE_PHONE_DISPLAY = "+64 9 886 4856";
export const TALK_TO_GENIE_PHONE_TEL = "+6498864856";

/** Section 3 — "What the magic actually does." */
export const MAGIC_PILLARS: MagicPillar[] = [
  {
    id: "connected",
    title: "Connected to your systems",
    description:
      "Your CRM, calendar, inbox, stock system, roster. The genie reads from them and writes back, without anyone copying anything across. It's your business's brain, not a chatbot bolted on top.",
    genieIcon: "sync",
  },
  {
    id: "live",
    title: "Live in an hour",
    description:
      "A chat conversation, not an integration project. No developer. No partner. No timeline.",
    genieIcon: "genie-lamp",
  },
  {
    id: "trained",
    title: "Trained how you need",
    description:
      "On your documents: FAQs, pricing, install guides, SOPs, the handbook. Update the source and the genie picks up the new answer instantly.",
    genieIcon: "book-open",
  },
  {
    id: "acts",
    title: "Acts how you want",
    description:
      "Your voice, your tone, your escalation rules, your brand. Lock the playbook once and the genie holds the line on every call, chat, and email, the same way you would.",
    genieIcon: "palette",
  },
  {
    id: "channels",
    title: "One brain, every channel",
    description:
      "The customer who chats at 4pm and rings at 6pm picks up where they left off. Same brain across voice, chat, phone, QR, website, and email. One memory, one voice, everywhere.",
    genieIcon: "globe",
  },
];

/** Section 4 — the first layer of support. */
export const BUSINESS_NUMBERS: BusinessNumber[] = [
  {
    id: "resolved",
    display: "80%",
    animateTo: 80,
    suffix: "%",
    label:
      "of everyday support questions, resolved on their own. Help Genie is the first layer your customers and team reach for, answered the moment they ask.",
  },
  {
    id: "manuals",
    display: "Goodbye manuals",
    label:
      "No more digging through PDFs, FAQs, or handbooks. People just ask in plain language and get the exact answer, pulled straight from your own content.",
  },
  {
    id: "your-ai",
    display: "Your AI, not some AI",
    label:
      "Trained only on what your business knows. Every reply is on-brand, in your voice, and grounded in your truth, never a generic off-the-shelf bot.",
  },
  {
    id: "always-on",
    display: "24/7",
    label:
      "Every hour, any language, across voice, chat, phone, website, and email. Your first line of support never clocks off.",
  },
];

/** Section 6 — the seven channels. */
export const CHANNELS: Channel[] = [
  {
    id: "voice-chat",
    name: "Voice + Chat",
    tagline:
      "Phone calls and web conversations. Same brain, same voice, answered in under two seconds.",
    replaces: "the receptionist you can't afford and the voicemail nobody checks.",
    status: "live",
    genieIcon: "microphone",
    href: "/capabilities/voice-experiences",
  },
  {
    id: "phone",
    name: "Phone numbers",
    tagline:
      "Bring your own or get a new one: local or toll-free, provisioned in minutes.",
    replaces: "the line that goes to voicemail after hours.",
    status: "live",
    genieIcon: "phone",
    href: "/talk-to-genie",
  },
  {
    id: "qr",
    name: "QR codes",
    tagline:
      "Print them anywhere. Customers scan and the conversation starts. No app install.",
    replaces: "the manual on the back of the warranty card.",
    status: "live",
    genieIcon: "qr-code",
    href: "/talk-to-genie",
  },
  {
    id: "website",
    name: "Website widget",
    tagline:
      "One line of code drops the genie onto any page, branded to your site.",
    replaces: "the live-chat widget you pay monthly for.",
    status: "live",
    genieIcon: "code",
    href: "/capabilities/sales-lead-capture",
  },
  {
    id: "mobile",
    name: "Mobile app",
    tagline:
      "Every genie you've spun up, in your pocket. Invite the team, share access with anyone.",
    replaces: "the desk you check between jobs.",
    status: "live",
    genieIcon: "dashboard",
  },
  {
    id: "email",
    name: "Email",
    tagline:
      "Reads, classifies, and prioritises every email, then drafts a reply your team sends with one click.",
    replaces: "the inbox that never empties.",
    status: "early",
    genieIcon: "mail",
  },
  {
    id: "outbound",
    name: "Outbound",
    tagline:
      "The genie calls the customer first: check-ins, confirmations, welcome calls, real feedback.",
    replaces: "the post-job survey nobody fills in.",
    status: "early",
    genieIcon: "share",
  },
];

/** Section 7 — three steps to ship. */
export const SHIP_STEPS: ShipStep[] = [
  {
    number: "01",
    title: "Configure",
    description:
      "Open a chat with the genie builder. Tell it about your products, services, who it serves, and the tone to use.",
    genieIcon: "settings",
  },
  {
    number: "02",
    title: "Connect",
    description:
      "Drop in your existing materials: FAQ pages, price lists, websites, policies, manuals, SOPs. The genie ingests them.",
    genieIcon: "files",
  },
  {
    number: "03",
    title: "Launch",
    description:
      "Brand it with your colours. Turn on the channels. The genie is live on your number, website, and printed QRs within the hour.",
    genieIcon: "globe",
  },
];

/** Section 8 — Sync, before / during / after the call. */
export const SYNC_PHASES: SyncPhase[] = [
  {
    id: "before",
    label: "Before the call",
    title: "Knows your business before it picks up",
    description:
      "Reads from your knowledge base, your pricing, your roster, your stock system. It knows your business before it says hello.",
    genieIcon: "book-open",
  },
  {
    id: "during",
    label: "During the call",
    title: "Mid-conversation lookups, in real time",
    description:
      "Check stock. Find availability. Pull a customer record. Real answers, in real time, from your real data.",
    genieIcon: "eye",
  },
  {
    id: "after",
    label: "After the call",
    title: "Writes back where it needs to land",
    description:
      "Job in the calendar. Lead in the CRM. Disposition logged to the right place. A ping for the team that needs to know.",
    genieIcon: "sync",
  },
];

/** Section 10 — the same questions, two very different days. */
export const ECONOMICS: EconomicsColumn[] = [
  {
    id: "today",
    kind: "human",
    heading: "The questions today",
    price: "9 to 5",
    priceNote: "One person, one call at a time. The rest wait or walk.",
    points: [
      { text: "Calls come in while your best person is mid-job. They pick up or they don't." },
      { text: "After 5pm it goes to voicemail nobody checks till morning." },
      { text: "The same fifteen questions, asked and answered, all day, every day." },
      { text: "The web lead at 11pm has gone cold by the time you see it." },
    ],
    scalesBy: "Gets heavier as you grow",
  },
  {
    id: "genie",
    kind: "genie",
    heading: "The questions with a genie",
    price: "24/7",
    priceNote:
      "Every question, every channel, answered the moment it's asked. In your voice.",
    points: [
      { text: "Every question answered in under two seconds, across phone, chat, and email." },
      { text: "2am, Sunday, mid-rush. Nobody gets sent to voicemail." },
      { text: "50 conversations at once, same brain, same answers." },
      { text: "That 11pm lead is qualified and in your CRM before you wake up." },
    ],
    scalesBy: "Handles 50 a day or 5,000 the same way",
  },
];

/** Section 9 — use cases. */
export const USE_CASES: UseCase[] = [
  {
    id: "external",
    kicker: "Customer-facing",
    title: "External support, 24/7",
    description:
      "The customer-facing layer across every channel. The genie answers questions, books work, captures leads, handles complaints, and escalates to a human when it matters.",
    points: [
      "Answers product questions from the manual, FAQ, and pricing pages",
      "Books urgent jobs into the calendar at 11pm",
      "Qualifies after-hours web leads and pushes them to your CRM",
      "Captures complaints in structured form, routed to the right human",
    ],
    genieIcon: "users",
    image: "/segment-pages/product-support/hero.webp",
    href: "/capabilities/product-support",
    ctaLabel: "Customer support",
  },
  {
    id: "internal",
    kicker: "Team-facing",
    title: "Internal support, your team's first call",
    description:
      "The repeat questions your team asks each other, answered from your handbook, SOPs, and internal procedures, on a phone or a chat, in the field or at the desk.",
    points: [
      "Trained on your employee handbook, SOPs, and procedures",
      "Available on a private line, web chat, or QR sticker",
      "Field teams ring for parts, codes, callbacks, install steps",
      "New hires ramp without burning a senior on the same questions",
    ],
    genieIcon: "brain",
    image: "/segment-pages/internal-information-delivery/hero.webp",
    href: "/capabilities/internal-information-delivery",
    ctaLabel: "Internal support",
  },
  {
    id: "manufacturers",
    kicker: "Product-facing",
    title: "Put a genie on the product",
    description:
      "Every product ships with documentation customers never read. Put a Help Genie on it and the product becomes talkable, answered from the actual source of truth.",
    points: [
      "Point the genie at the install guide, owner manual, and spec sheet",
      "Print a QR on the box, the product, the warranty card",
      "Customers ask in voice or chat; the genie answers in plain language",
      "Update the source, and every customer gets the new answer instantly",
    ],
    genieIcon: "wrench",
    image: "/solutions-pages/manufacturing/hero.webp",
    href: "/manufacturing",
    ctaLabel: "Manufacturing",
  },
];

/**
 * Section 12 — who it's for. We lead with the *situation* every SME recognises,
 * not an industry catalogue. These are the "if this is your day, a genie fits" signals.
 */
export const FIT_SIGNALS: string[] = [
  "The same questions, asked over and over.",
  "Customers reaching you across three different channels.",
  "Not enough hours in the day to answer them all.",
  "Nobody free to pick up after six.",
];

/**
 * Industries kept only as *examples of the pattern*, not as billboard cards.
 * Rendered as a light linked strip for navigation and SEO; slugs map to /solutions/:slug.
 */
export const HOME_INDUSTRIES: HomeIndustry[] = [
  { slug: "trades", name: "Trades", tagline: "Books jobs and handles calls while you work.", icon: "/icons/solutions/icon-trades.png" },
  { slug: "automotive", name: "Automotive", tagline: "Drives sales and service excellence.", icon: "/icons/solutions/icon-automotive.png" },
  { slug: "marine", name: "Marine", tagline: "Navigates customer engagement.", icon: "/icons/solutions/icon-marine.png" },
  { slug: "appliances", name: "Appliances", tagline: "Powers up customer service.", icon: "/icons/solutions/icon-appliances.png" },
  { slug: "office-equipment", name: "Office Equipment", tagline: "Keeps your office running smoothly.", icon: "/icons/solutions/icon-office-equipment.png" },
  { slug: "home-builders", name: "Home Builders", tagline: "Supports homeowners from build to living.", icon: "/icons/solutions/icon-home-builders.png" },
  { slug: "travel-hospitality", name: "Travel & Hospitality", tagline: "Elevates every guest experience.", icon: "/icons/solutions/icon-travel-hospitality.png" },
  { slug: "manufacturing", name: "Manufacturing", tagline: "Streamlines operations, boosts productivity.", icon: "/icons/solutions/icon-manufacturing.png" },
  { slug: "industrial", name: "Industrial", tagline: "Keeps operations running 24/7.", icon: "/icons/solutions/icon-industrial.png" },
  { slug: "mining", name: "Mining", tagline: "Keeps mines running 24/7.", icon: "/icons/solutions/icon-industrial.png" },
  { slug: "real-estate", name: "Real Estate", tagline: "Never misses a listing enquiry.", icon: "/icons/solutions/icon-real-estate.png" },
  { slug: "events", name: "Events", tagline: "Makes every event unforgettable.", icon: "/icons/solutions/icon-tours-activities.png" },
];
